import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { DeletePinControls } from "@/components/DeletePinControls";
import {
  Plus, Link2, RefreshCw, KeyRound, Eye, Check, Edit, Trash2,
  AlertTriangle, Info, Truck, Store as StoreIcon, Image as ImageIcon,
  FileSpreadsheet, UploadCloud, Loader2,
} from "lucide-react";

// CS 4.16 — Distributors. Same conceptual shape as Franchises but every distributor
// exposes TWO independent public links (Distributor + Retailer) and TWO PINs.
// Layout: stacked link blocks (option b — chosen by user).
export default function Distributors() {
  const { user: me } = useAuth();
  const canEdit = me?.role === "master_admin";
  const [list, setList] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState({
    name: "", city: "", contact_name: "", contact_phone: "", contact_email: "",
    distributor_pin: "1234", retailer_pin: "1234",
  });
  const [pinTarget, setPinTarget] = useState(null); // { distributor, which: 'distributor'|'retailer' }
  const [newPin, setNewPin] = useState("");
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [deletionImpact, setDeletionImpact] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deletingBusy, setDeletingBusy] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);
  const [copyFallback, setCopyFallback] = useState(null);
  const [photosFor, setPhotosFor] = useState(null); // { distributor } for photos modal

  const load = useCallback(() => api.get("/distributors").then((r) => setList(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!creating.name.trim()) return toast.error("Name is required");
    if (!/^\d{4}$/.test(creating.distributor_pin)) return toast.error("Distributor PIN must be 4 digits");
    if (!/^\d{4}$/.test(creating.retailer_pin)) return toast.error("Retailer PIN must be 4 digits");
    try {
      await api.post("/distributors", creating);
      toast.success("Distributor created");
      setShowCreate(false);
      setCreating({ name: "", city: "", contact_name: "", contact_phone: "", contact_email: "", distributor_pin: "1234", retailer_pin: "1234" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Create failed");
    }
  };

  const regenerate = async (id, which) => {
    const label = which === "distributor" ? "Distributor link" : which === "retailer" ? "Retailer link" : "Both links";
    if (!window.confirm(`Regenerate ${label}? The old link will stop working.`)) return;
    await api.post(`/distributors/${id}/regenerate-link`, { which });
    toast.success(`${label} regenerated`);
    load();
  };

  const setPin = async () => {
    if (!/^\d{4}$/.test(newPin)) return toast.error("PIN must be 4 digits");
    try {
      const payload = pinTarget.which === "distributor"
        ? { distributor_pin: newPin }
        : { retailer_pin: newPin };
      await api.post(`/distributors/${pinTarget.distributor.id}/set-pin`, payload);
      toast.success("PIN updated");
      setPinTarget(null);
      setNewPin("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const toggleActive = async (d) => {
    await api.patch(`/distributors/${d.id}`, { is_active: !d.is_active });
    load();
  };

  const buildLink = (token) => `${window.location.origin}/distributor/${token}`;
  const buildRetailerLink = (token) => `${window.location.origin}/retailer/${token}`;

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopiedKey((c) => (c === key ? null : c)), 2000);
    } catch {
      setCopyFallback(text);
    }
  };

  const saveEdit = async () => {
    if (!editing.name?.trim()) return toast.error("Name is required");
    try {
      await api.patch(`/distributors/${editing.id}`, {
        name: editing.name, city: editing.city,
        contact_name: editing.contact_name, contact_phone: editing.contact_phone, contact_email: editing.contact_email,
      });
      toast.success("Distributor details updated");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const openDelete = async (d) => {
    setDeleting(d);
    setDeleteConfirmText("");
    setDeletionImpact(null);
    try {
      const { data } = await api.get(`/distributors/${d.id}/deletion-impact`);
      setDeletionImpact(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to fetch impact");
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    if (deleteConfirmText.trim() !== deleting.name) {
      return toast.error("Distributor name does not match");
    }
    setDeletingBusy(true);
    try {
      const { data } = await api.delete(`/distributors/${deleting.id}`);
      toast.success(`Deleted ${deleting.name}. ${data.products_reassigned || 0} products moved to Unassigned.`);
      setDeleting(null);
      setDeletionImpact(null);
      setDeleteConfirmText("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to delete");
    } finally {
      setDeletingBusy(false);
    }
  };

  const renderLinkBlock = ({ icon: Icon, accent, title, subtitle, url, pin, distributorId, which, distributor }) => {
    const copyKey = `${distributorId}-${which}`;
    return (
      <div
        className="rounded-md border border-[#0D1B2A]/10 p-4 bg-white"
        data-testid={`${which}-link-block-${distributorId}`}
        style={{ borderLeft: `3px solid ${accent}` }}
      >
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <div className="flex items-center gap-2">
            <Icon className="w-4 h-4" strokeWidth={1.5} style={{ color: accent }} />
            <div>
              <div className="text-sm font-medium text-[#0D1B2A]">{title}</div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400">{subtitle}</div>
            </div>
          </div>
          <div className="text-xs text-slate-500">
            PIN: <span className="font-mono text-[#0D1B2A]">{pin || "—"}</span>
          </div>
        </div>
        <div className="text-xs text-slate-500 font-mono break-all mb-3 select-all" data-testid={`${which}-link-url-${distributorId}`}>
          {url}
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => copy(url, copyKey)}
            data-testid={`${which}-copy-${distributorId}`}
            className={`px-3 py-1.5 rounded-md border text-xs flex items-center gap-1.5 transition-colors ${
              copiedKey === copyKey
                ? "border-emerald-500 text-emerald-600 bg-emerald-50"
                : "border-[#0D1B2A]/15 hover:bg-[#FAF8F3]"
            }`}
          >
            {copiedKey === copyKey ? <><Check className="w-3.5 h-3.5" /> Copied!</> : <><Link2 className="w-3.5 h-3.5" /> Copy</>}
          </button>
          <button
            onClick={() => window.open(url, "_blank")}
            data-testid={`${which}-preview-${distributorId}`}
            className="px-3 py-1.5 rounded-md border border-[#0D1B2A]/15 text-xs flex items-center gap-1.5 hover:bg-[#FAF8F3]"
          >
            <Eye className="w-3.5 h-3.5" /> Preview
          </button>
          {canEdit && (
            <button
              onClick={() => regenerate(distributorId, which)}
              data-testid={`${which}-regenerate-${distributorId}`}
              className="px-3 py-1.5 rounded-md border border-[#0D1B2A]/15 text-xs flex items-center gap-1.5 hover:bg-[#FAF8F3]"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Regenerate
            </button>
          )}
          {canEdit && (
            <button
              onClick={() => { setPinTarget({ distributor, which }); setNewPin(""); }}
              data-testid={`${which}-pin-${distributorId}`}
              className="px-3 py-1.5 rounded-md border border-[#0D1B2A]/15 text-xs flex items-center gap-1.5 hover:bg-[#FAF8F3]"
            >
              <KeyRound className="w-3.5 h-3.5" /> PIN
            </button>
          )}
        </div>
        <div className="mt-3 pt-3 border-t border-[#0D1B2A]/5">
          <DeletePinControls
            entity={distributor}
            basePath={`/distributors/${distributorId}`}
            canEdit={canEdit}
            onChanged={load}
            testidPrefix={`${which}-link-${distributorId}`}
            sharedNote="One delete PIN covers both the Distributor and Retailer links."
          />
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-8" data-testid="distributors-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#C9A84C] mb-2">Network</p>
          <h1 className="font-serif text-4xl md:text-5xl text-[#0D1B2A]">Distributors</h1>
          <p className="text-slate-500 mt-2">Manage distributor partners with separate Distributor and Retailer access links.</p>
        </div>
        {canEdit && (
          <Button onClick={() => setShowCreate(true)} className="bg-[#C9A84C] hover:bg-[#C9A84C]/90 text-white" data-testid="create-distributor-btn">
            <Plus className="w-4 h-4 mr-2" /> Add distributor
          </Button>
        )}
      </div>

      {!canEdit && (
        <div className="luxe-card p-4 bg-amber-50/50 border-l-4 border-amber-300 flex items-start gap-3" data-testid="distributors-readonly-banner">
          <Info className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
          <p className="text-sm text-amber-800">Viewing distributor list (read-only). Contact a Master Admin to modify distributor details or links.</p>
        </div>
      )}

      <div className="grid gap-6">
        {list === null && Array.from({ length: 2 }).map((_, i) => <div key={i} className="skeleton h-64" />)}
        {list && list.length === 0 && (
          <div className="luxe-card p-10 text-center text-slate-400" data-testid="distributors-empty">
            <Truck className="w-10 h-10 mx-auto mb-3 text-slate-300" strokeWidth={1.5} />
            <p>No distributors yet.</p>
            {canEdit && <p className="text-xs mt-2">Click &quot;Add distributor&quot; to create your first one.</p>}
          </div>
        )}
        {list && list.map((d) => {
          const distLink = buildLink(d.distributor_link_token);
          const retailLink = buildRetailerLink(d.retailer_link_token);
          return (
            <div key={d.id} className="luxe-card p-6 space-y-5" data-testid={`distributor-${d.id}`}>
              {/* Distributor identity header */}
              <div className="flex flex-wrap items-start justify-between gap-4 pb-4 border-b border-[#0D1B2A]/5">
                <div>
                  <div className="flex items-center gap-3">
                    <h3 className="font-serif text-2xl text-[#0D1B2A]">{d.name}</h3>
                    <span className={`text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full ${d.is_active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>
                      {d.is_active ? "Active" : "Inactive"}
                    </span>
                  </div>
                  <div className="text-sm text-slate-500 mt-1">
                    {[d.city, d.contact_name, d.contact_phone].filter(Boolean).join(" · ")}
                  </div>
                  <div className="text-xs text-slate-400 mt-1">{d.product_count || 0} products</div>
                </div>
              </div>

              {/* Stacked link blocks (option b) */}
              {renderLinkBlock({
                icon: Truck,
                accent: "#0D1B2A",
                title: "Distributor Link",
                subtitle: "Distributor owner view — sees company → distributor pricing",
                url: distLink,
                pin: d.distributor_pin_plain,
                distributorId: d.id,
                which: "distributor",
                distributor: d,
              })}
              {renderLinkBlock({
                icon: StoreIcon,
                accent: "#C9A84C",
                title: "Retailer Link",
                subtitle: "Shared with all retailers under this distributor",
                url: retailLink,
                pin: d.retailer_pin_plain,
                distributorId: d.id,
                which: "retailer",
                distributor: d,
              })}

              {/* Card-level actions */}
              {canEdit && (
                <div className="flex flex-wrap gap-2 pt-2">
                  <button
                    onClick={() => setEditing({ ...d })}
                    data-testid={`edit-distributor-${d.id}`}
                    className="px-3 py-2 rounded-md border border-[#0D1B2A]/15 text-sm flex items-center gap-2 hover:bg-[#FAF8F3]"
                  >
                    <Edit className="w-4 h-4" /> Edit details
                  </button>
                  <button
                    onClick={() => toggleActive(d)}
                    data-testid={`toggle-active-${d.id}`}
                    className="px-3 py-2 rounded-md border border-[#0D1B2A]/15 text-sm hover:bg-[#FAF8F3]"
                  >
                    {d.is_active ? "Deactivate" : "Activate"}
                  </button>
                  <button
                    onClick={() => setPhotosFor(d)}
                    data-testid={`photos-distributor-${d.id}`}
                    className="px-3 py-2 rounded-md border border-[#0D1B2A]/15 text-sm flex items-center gap-2 hover:bg-[#FAF8F3]"
                  >
                    <ImageIcon className="w-4 h-4" /> Photos
                  </button>
                  <button
                    onClick={() => openDelete(d)}
                    data-testid={`delete-distributor-${d.id}`}
                    className="px-3 py-2 rounded-md border border-red-300 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" /> Delete
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Create modal */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-serif text-2xl">New Distributor</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Name" value={creating.name} onChange={(e) => setCreating({ ...creating, name: e.target.value })} data-testid="create-distributor-name" />
            <Input placeholder="City" value={creating.city} onChange={(e) => setCreating({ ...creating, city: e.target.value })} data-testid="create-distributor-city" />
            <Input placeholder="Contact name" value={creating.contact_name} onChange={(e) => setCreating({ ...creating, contact_name: e.target.value })} />
            <Input placeholder="Phone" value={creating.contact_phone} onChange={(e) => setCreating({ ...creating, contact_phone: e.target.value })} />
            <Input placeholder="Email" value={creating.contact_email} onChange={(e) => setCreating({ ...creating, contact_email: e.target.value })} />
            <div className="grid grid-cols-2 gap-3 pt-2 border-t border-[#0D1B2A]/5">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Distributor PIN (4 digits)</label>
                <Input
                  value={creating.distributor_pin}
                  onChange={(e) => setCreating({ ...creating, distributor_pin: e.target.value.replace(/\D/g, "").slice(0, 4) })}
                  maxLength={4}
                  inputMode="numeric"
                  className="font-mono text-center tracking-[0.5em]"
                  data-testid="create-distributor-pin"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Retailer PIN (4 digits)</label>
                <Input
                  value={creating.retailer_pin}
                  onChange={(e) => setCreating({ ...creating, retailer_pin: e.target.value.replace(/\D/g, "").slice(0, 4) })}
                  maxLength={4}
                  inputMode="numeric"
                  className="font-mono text-center tracking-[0.5em]"
                  data-testid="create-distributor-retailer-pin"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={create} className="bg-[#0D1B2A] text-white hover:bg-[#0D1B2A]/90" data-testid="create-distributor-submit">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PIN modal */}
      <Dialog open={!!pinTarget} onOpenChange={(o) => { if (!o) { setPinTarget(null); setNewPin(""); } }}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              Set {pinTarget?.which === "distributor" ? "Distributor" : "Retailer"} PIN
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">
            4-digit PIN used to gate the {pinTarget?.which === "distributor" ? "distributor owner" : "retailer"} public link for <span className="font-medium text-[#0D1B2A]">{pinTarget?.distributor?.name}</span>.
          </p>
          <Input
            value={newPin}
            onChange={(e) => setNewPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
            maxLength={4}
            inputMode="numeric"
            placeholder="0000"
            className="text-2xl tracking-[0.5em] text-center h-14"
            data-testid="pin-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPinTarget(null); setNewPin(""); }}>Cancel</Button>
            <Button onClick={setPin} className="bg-[#0D1B2A] text-white hover:bg-[#0D1B2A]/90" data-testid="pin-save">Save PIN</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CS 21.23 -- Photos modal mirrors the Wholesaler photos tab exactly.
          Two upload options: (1) embedded-image .xlsx photo book (primary),
          (2) filename-based bulk upload. Both map to this distributor's
          distributor_stock rows only. */}
      <DistributorPhotosModal
        distributor={photosFor}
        onClose={() => setPhotosFor(null)}
      />

      {/* Copy fallback */}
      <Dialog open={!!copyFallback} onOpenChange={(o) => { if (!o) setCopyFallback(null); }}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Copy this link</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Your browser blocked automatic copy. Select the link below and copy manually.</p>
          <Input value={copyFallback || ""} readOnly className="font-mono text-xs" onFocus={(e) => e.target.select()} />
        </DialogContent>
      </Dialog>

      {/* Edit modal */}
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="bg-white" data-testid="edit-distributor-modal">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Edit distributor details</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Distributor name</label>
                <Input value={editing.name || ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="edit-distributor-name" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">City</label>
                <Input value={editing.city || ""} onChange={(e) => setEditing({ ...editing, city: e.target.value })} data-testid="edit-distributor-city" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Contact name</label>
                <Input value={editing.contact_name || ""} onChange={(e) => setEditing({ ...editing, contact_name: e.target.value })} />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Phone</label>
                <Input value={editing.contact_phone || ""} onChange={(e) => setEditing({ ...editing, contact_phone: e.target.value })} />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Email</label>
                <Input type="email" value={editing.contact_email || ""} onChange={(e) => setEditing({ ...editing, contact_email: e.target.value })} />
              </div>
              <p className="text-[10px] text-slate-400 pt-2 border-t border-[#0D1B2A]/5">
                Use the Regenerate / PIN buttons inside each link block to rotate links or PINs.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={saveEdit} className="bg-[#0D1B2A] hover:bg-[#0D1B2A]/90 text-white" data-testid="edit-distributor-save">Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleting} onOpenChange={(o) => { if (!o && !deletingBusy) { setDeleting(null); setDeletionImpact(null); setDeleteConfirmText(""); } }}>
        <DialogContent className="bg-white max-w-lg" data-testid="delete-distributor-modal">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-6 h-6" /> Delete distributor
            </DialogTitle>
          </DialogHeader>
          {deleting && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                You are about to permanently delete <span className="font-semibold text-[#0D1B2A]">{deleting.name}</span>.
                This action cannot be undone.
              </p>
              <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm space-y-2">
                <div className="text-red-800 font-medium mb-1">What will happen</div>
                {deletionImpact ? (
                  <ul className="text-red-700 list-disc pl-5 space-y-1">
                    <li>
                      <span className="font-mono font-semibold">{deletionImpact.product_count}</span> product{deletionImpact.product_count === 1 ? "" : "s"} will be moved to <span className="font-semibold">Unassigned</span>.
                    </li>
                    <li>
                      <span className="font-mono font-semibold">{deletionImpact.user_count}</span> user account{deletionImpact.user_count === 1 ? "" : "s"} will be deactivated.
                    </li>
                    <li>Both shareable links (Distributor + Retailer) will stop working.</li>
                  </ul>
                ) : (
                  <div className="text-red-700">Loading impact…</div>
                )}
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">
                  Type <span className="font-mono text-red-700">{deleting.name}</span> to confirm
                </label>
                <Input
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder={deleting.name}
                  data-testid="delete-confirm-input"
                  autoFocus
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeleting(null); setDeletionImpact(null); setDeleteConfirmText(""); }} disabled={deletingBusy}>
              Cancel
            </Button>
            <Button
              onClick={confirmDelete}
              disabled={deletingBusy || !deleting || deleteConfirmText.trim() !== deleting?.name}
              className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
              data-testid="delete-confirm-btn"
            >
              {deletingBusy ? "Deleting…" : "Delete distributor"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Photos modal — mirrors the Wholesaler photos section exactly. ───────
function DistributorPhotosModal({ distributor, onClose }) {
  const [xlsxFile, setXlsxFile] = useState(null);
  const [xlsxBusy, setXlsxBusy] = useState(false);
  const [xlsxResult, setXlsxResult] = useState(null);
  const [bulkFiles, setBulkFiles] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  useEffect(() => {
    // Reset state whenever the modal opens for a different distributor.
    setXlsxFile(null); setXlsxResult(null); setXlsxBusy(false);
    setBulkFiles([]); setBulkResult(null); setBulkBusy(false);
  }, [distributor?.id]);

  if (!distributor) return null;
  const did = distributor.id;

  const uploadXlsx = async () => {
    if (!xlsxFile) return toast.error("Pick a .xlsx workbook");
    setXlsxBusy(true); setXlsxResult(null);
    try {
      const fd = new FormData();
      fd.append("file", xlsxFile);
      const { data } = await api.post(
        `/distributors/${did}/photos/xlsx-embedded`, fd, { timeout: 600000 });
      setXlsxResult(data);
      toast.success(
        `Mapped ${data.mapped_count} · ${data.rows_without_image_count} without image · ${data.unmatched_count} unmatched`,
      );
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally { setXlsxBusy(false); }
  };

  const uploadBulk = async () => {
    if (!bulkFiles.length) return toast.error("Pick one or more image files");
    setBulkBusy(true); setBulkResult(null);
    try {
      const fd = new FormData();
      bulkFiles.forEach((f) => fd.append("files", f));
      const { data } = await api.post(`/distributors/${did}/photos/bulk`, fd);
      setBulkResult(data);
      toast.success(`Linked ${data.matched_count} · ${data.unmatched_count} unmatched`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally { setBulkBusy(false); }
  };

  return (
    <Dialog open={!!distributor} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid={`photos-modal-${did}`}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">
            Photos — <span className="text-[#C9A84C]">{distributor.name}</span>
          </DialogTitle>
        </DialogHeader>

        {/* Section 1: Embedded-image xlsx (PRIMARY) */}
        <div className="rounded-md border border-[#0D1B2A]/10 p-4 space-y-3" data-testid="photos-xlsx-section">
          <div className="flex items-center gap-2 text-sm">
            <FileSpreadsheet className="w-4 h-4 text-[#C9A84C]" />
            <span className="font-medium">Photo book (embedded-image .xlsx)</span>
            <span className="text-[10px] uppercase tracking-[0.2em] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
              Primary
            </span>
          </div>
          <p className="text-xs text-slate-500">
            Column&nbsp;A = embedded image, col&nbsp;B = <span className="font-mono">JewelCode</span>,
            col&nbsp;C = <span className="font-mono">StyleCode</span>. Matched by row anchor,
            JewelCode first then StyleCode; de-duped by SHA-256.
          </p>
          <div className="flex flex-wrap gap-3 items-center">
            <Input
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(e) => { setXlsxFile(e.target.files?.[0] || null); setXlsxResult(null); }}
              disabled={xlsxBusy}
              data-testid="dist-photos-xlsx-input"
              className="flex-1 min-w-[220px]"
            />
            <Button
              onClick={uploadXlsx} disabled={!xlsxFile || xlsxBusy}
              className="bg-[#0D1B2A] hover:bg-[#0D1B2A]/90 text-white"
              data-testid="dist-photos-xlsx-upload-btn"
            >
              {xlsxBusy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Parsing…</> : "Upload & map"}
            </Button>
          </div>
          {xlsxResult && (
            <div className="text-xs text-slate-600 space-y-1 pt-2 border-t border-slate-100" data-testid="dist-photos-xlsx-result">
              <div>
                <span className="font-medium">{xlsxResult.mapped_count}</span> mapped ·{" "}
                <span className="text-amber-700">{xlsxResult.rows_without_image_count}</span> without image ·{" "}
                <span className="text-amber-700">{xlsxResult.unmatched_count}</span> unmatched ·{" "}
                <span className="text-slate-500">{xlsxResult.dedupe_unique_blobs}</span> unique blobs
              </div>
              <div className="text-[11px] text-slate-500">
                Match breakdown: <span className="font-mono">{xlsxResult.matched_by_jewel_code_count}</span> by JewelCode ·{" "}
                <span className="font-mono">{xlsxResult.matched_by_style_code_count}</span> by StyleCode fallback
              </div>
            </div>
          )}
        </div>

        {/* Section 2: Bulk filename upload */}
        <div className="rounded-md border border-[#0D1B2A]/10 p-4 space-y-3 mt-4" data-testid="photos-bulk-section">
          <div className="flex items-center gap-2 text-sm">
            <ImageIcon className="w-4 h-4 text-[#C9A84C]" />
            <span className="font-medium">Bulk photo upload</span>
          </div>
          <p className="text-xs text-slate-500">
            Drop image files whose filename matches a jewel code (e.g. <span className="font-mono">25P3886.jpg</span>) or
            style number. Only this distributor&apos;s stock is updated.
          </p>
          <Input
            type="file" multiple accept="image/*"
            onChange={(e) => setBulkFiles(Array.from(e.target.files || []))}
            disabled={bulkBusy}
            data-testid="dist-photos-bulk-input"
          />
          {bulkFiles.length > 0 && (
            <div className="text-xs text-slate-500">
              {bulkFiles.length} file{bulkFiles.length === 1 ? "" : "s"} selected
              <button onClick={() => setBulkFiles([])} className="ml-2 text-[#0D1B2A] underline-offset-2 hover:underline">
                clear
              </button>
            </div>
          )}
          <div className="flex justify-end">
            <Button
              onClick={uploadBulk} disabled={!bulkFiles.length || bulkBusy}
              className="bg-[#0D1B2A] hover:bg-[#0D1B2A]/90 text-white"
              data-testid="dist-photos-bulk-upload-btn"
            >
              {bulkBusy ? "Uploading…" : `Upload ${bulkFiles.length || ""} & auto-map`}
            </Button>
          </div>
          {bulkResult && (
            <div className="text-xs text-slate-600 space-y-1 pt-2 border-t border-slate-100" data-testid="dist-photos-bulk-result">
              <div>
                <span className="font-medium">{bulkResult.matched_count}</span> matched ·{" "}
                <span className="text-amber-700">{bulkResult.unmatched_count}</span> unmatched
              </div>
              {bulkResult.unmatched_count > 0 && (
                <details className="text-[11px] text-amber-700">
                  <summary className="cursor-pointer">Show unmatched filenames</summary>
                  <ul className="mt-2 max-h-40 overflow-y-auto space-y-0.5 font-mono">
                    {bulkResult.unmatched.map((u) => (
                      <li key={u.filename}>{u.filename}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
